import {
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  Select,
} from '@chakra-ui/react';
import { useFormContext } from 'react-hook-form';

import { IntegrationConfigField } from '@aslanpay/portal-common-lib';

import { INTEGRATION_TYPE_OPTIONS } from './constants';

const integrationTypeLabel = (integrationType: string) =>
  INTEGRATION_TYPE_OPTIONS.find((option) => option.value === integrationType)?.text ??
  integrationType;

interface ConfigSpecFieldInputProps {
  field: IntegrationConfigField;
}

/** One dedicated input for a provider's spec'd config field (dropdown for SELECT, text otherwise) -
 * shared by ConnectionSettingsFields and AddIntegrationTypeModal. Expects a `configValues.<key>` entry
 * within the surrounding react-hook-form context. */
function ConfigSpecFieldInput({ field }: ConfigSpecFieldInputProps) {
  const {
    register,
    formState: { errors },
  } = useFormContext<{ configValues?: Record<string, string> }>();
  const error = errors.configValues?.[field.key];
  const validationRules = field.required ? { required: `${field.key} is required` } : undefined;

  return (
    <FormControl isInvalid={!!error} isRequired={field.required}>
      <FormLabel>
        {field.key}
        {field.integrationType ? ` (${integrationTypeLabel(field.integrationType)})` : ''}
      </FormLabel>
      {field.fieldType === 'SELECT' ? (
        <Select placeholder="Not set" {...register(`configValues.${field.key}`, validationRules)}>
          {(field.options ?? []).map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </Select>
      ) : (
        <Input {...register(`configValues.${field.key}`, validationRules)} />
      )}
      {field.description && <FormHelperText>{field.description}</FormHelperText>}
      <FormErrorMessage>{error?.message}</FormErrorMessage>
    </FormControl>
  );
}

export default ConfigSpecFieldInput;
